import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../contexts/UserContext';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';
import { toast } from 'react-hot-toast';
import { usePasskeyChallenge } from '../../hooks/usePasskeyChallenge';
import {
  UserIcon,
  ComputerDesktopIcon,
  GlobeAltIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import AccountInfoTab from '../Settings/AccountInfoTab';

const UserProfile: React.FC = () => {
  const { t } = useTranslation('profile');
  const { profile, sessions, getSessions, revokeSession } = useUser();
  const { user } = useAuth();
  const location = useLocation();
  
  // Active tab state - will be synced with localStorage via useEffect
  const [activeTab, setActiveTabState] = useState<'info' | 'sessions'>('info');
  
  // Sync tab state with localStorage when on profile page
  useEffect(() => {
    const isProfilePage = location.pathname === '/profile';
    if (isProfilePage) {
      const saved = localStorage.getItem('profile_activeTab');
      if (saved && (saved === 'info' || saved === 'sessions')) {
        setActiveTabState(saved);
      } else {
        setActiveTabState('info');
      }
    } else {
      setActiveTabState('info');
    }
  }, [location.pathname]);
  
  const setActiveTab = (tab: 'info' | 'sessions') => {
    setActiveTabState(tab);
    localStorage.setItem('profile_activeTab', tab);
  };
  
  const [showSessionRevokeModal, setShowSessionRevokeModal] = useState(false);
  const [showLogoutAllModal, setShowLogoutAllModal] = useState(false);
  const [sessionToRevoke, setSessionToRevoke] = useState<{ id: string; deviceName: string } | null>(null);
  
  const endSessionChallenge = usePasskeyChallenge('end_session');
  const endAllSessionsChallenge = usePasskeyChallenge('end_all_sessions');

  useEffect(() => {
    if (activeTab === 'sessions') {
      getSessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  if (!profile || !user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-governate-500"></div>
      </div>
    );
  }

  const confirmSessionRevoke = async () => {
    if (!sessionToRevoke) return;

    const success = await endSessionChallenge.executeWithChallenge(async () => {
      try {
        await revokeSession(sessionToRevoke.id);
        await getSessions(); // Refresh sessions list
      setShowSessionRevokeModal(false);
      setSessionToRevoke(null);
        toast.success(t('toast.sessionEnded'));
      } catch (error) {
        console.error('Failed to revoke session:', error);
        toast.error(t('toast.sessionEndFailed'));
        throw error;
      }
    });

    if (!success) {
      // Passkey challenge failed or was cancelled
      return;
    }
  };

  const cancelSessionRevoke = () => {
    setShowSessionRevokeModal(false);
    setSessionToRevoke(null);
  };

  const handleLogoutAllOthers = () => {
    setShowLogoutAllModal(true);
  };

  const confirmLogoutAllOthers = async () => {
    const success = await endAllSessionsChallenge.executeWithChallenge(async () => {
    try {
      await apiService.revokeAllOtherSessions();
      await getSessions(); // Refresh sessions list
      setShowLogoutAllModal(false);
      toast.success(t('toast.allSessionsRevoked'));
    } catch (error) {
      console.error('Failed to revoke all other sessions:', error);
      toast.error(t('toast.allSessionsRevokeFailed'));
        throw error;
      }
    });

    if (!success) {
      // Passkey challenge failed or was cancelled
      return;
    }
  };

  const cancelLogoutAllOthers = () => {
    setShowLogoutAllModal(false);
  };

  const handleRevokeSession = async (sessionId: string) => {
    const success = await endSessionChallenge.executeWithChallenge(async () => {
    try {
      await revokeSession(sessionId);
      await getSessions(); // Refresh sessions list
      toast.success(t('toast.sessionEnded'));
    } catch (error) {
      console.error('Failed to revoke session:', error);
      toast.error(t('toast.sessionEndFailed'));
        throw error;
      }
    });

    if (!success) {
      // Passkey challenge failed or was cancelled
      return;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-black rounded-lg shadow-sm border border-gray-200 dark:border-gray-900 p-6">
        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 bg-governate-100 rounded-full flex items-center justify-center">
            <UserIcon className="w-8 h-8 text-governate-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{profile.name}</h1>
            <p className="text-gray-600 dark:text-gray-400">{profile.email}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-black rounded-lg shadow-sm border border-gray-200 dark:border-gray-900">
        <div className="border-b border-gray-200 dark:border-gray-800">
          <nav className="-mb-px flex space-x-8 px-6">
            {[
              { id: 'info', name: t('tabs.info'), count: null },
              { id: 'sessions', name: t('tabs.sessions'), count: sessions.length },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as 'info' | 'sessions')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-governate-500 text-governate-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.name}
                {tab.count !== null && (
                  <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Info Tab (account info: name, email, role, password, delete) */}
          {activeTab === 'info' && <AccountInfoTab />}

          {/* Sessions Tab */}
          {activeTab === 'sessions' && (
            <div className="space-y-4">
              {sessions.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-gray-700">
                      {t('sessions.activeSessions')} ({sessions.length})
                    </h3>
                    {sessions.length > 1 && (
                      <button
                        onClick={handleLogoutAllOthers}
                        className="text-sm text-red-600 hover:text-red-700 font-medium"
                      >
                        {t('sessions.logoutFromAllOtherDevices')}
                      </button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {[...sessions].sort((a, b) => {
                      // Sort current session to the top
                      if (a.is_current && !b.is_current) return -1;
                      if (!a.is_current && b.is_current) return 1;
                      return 0;
                    }).map((session) => {
                      // Debug logging
                      console.log('Rendering session:', session.id, 'Is current:', session.is_current, 'Should show button:', !session.is_current);
                      return (
                      <div key={session.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            {session.device_type === 'web' ? (
                              <GlobeAltIcon className="w-4 h-4 text-blue-600" />
                            ) : (
                              <ComputerDesktopIcon className="w-4 h-4 text-blue-600" />
                            )}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {session.device_name || t('sessions.unknownDevice')}
                            </div>
                            <div className="text-xs text-gray-500">
                              {session.device_type === 'web' ? t('sessions.webBrowser') : session.device_type} • 
                              {t('sessions.started')} {new Date(session.created_at).toLocaleDateString()} {new Date(session.created_at).toLocaleTimeString()}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                      
                          <div className="flex items-center space-x-2 mt-1">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              session.is_current 
                                ? 'bg-blue-100 text-blue-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {session.is_current ? t('sessions.current') : t('sessions.active')}
                            </span>
                            {!session.is_current && (
                              <button
                                onClick={() => handleRevokeSession(session.id)}
                                className="text-xs text-red-600 hover:text-red-800 font-medium"
                              >
                                {t('sessions.endSession')}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {sessions.length === 0 && (
                <p className="text-gray-500 text-center py-8">{t('sessions.noSessionsFound')}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Session Revoke Confirmation Modal */}
      {showSessionRevokeModal && sessionToRevoke && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              
              <div className="mt-2 px-7 py-3">
                <h3 className="text-lg font-medium text-gray-900">{t('modals.sessionRevoke.title')}</h3>
                <p className="text-sm text-gray-500 mt-2">
                  {t('modals.sessionRevoke.descriptionBefore')}<strong>{sessionToRevoke.deviceName}</strong>{t('modals.sessionRevoke.descriptionAfter')}
                </p>
              </div>
              
              <div className="items-center px-4 py-3">
                <button
                  onClick={cancelSessionRevoke}
                  className="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md w-24 mr-2 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  {t('modals.sessionRevoke.cancel')}
                </button>
                <button
                  onClick={confirmSessionRevoke}
                  className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md w-24 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  {t('modals.sessionRevoke.endSession')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Logout All Other Devices Confirmation Modal */}
      {showLogoutAllModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              
              <div className="mt-2 px-7 py-3">
                <h3 className="text-lg font-medium text-gray-900">{t('modals.logoutAll.title')}</h3>
                <p className="text-sm text-gray-500 mt-2">
                  {t('modals.logoutAll.description')}
                </p>
              </div>
              
              <div className="items-center px-4 py-3">
                <button
                  onClick={cancelLogoutAllOthers}
                  className="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md w-24 mr-2 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  {t('modals.logoutAll.cancel')}
                </button>
                <button
                  onClick={confirmLogoutAllOthers}
                  className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md w-24 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  {t('modals.logoutAll.logout')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default UserProfile;
